import type { SelectOption, MultiSelectOption } from "./types";
import type {
  GridcoDownloadFiltersResponse as FiltersResponse,
  GeneratorFilterItem,
} from "../../../Rest_api/RestApi";

// ─── Discriminated union — one shape per DataType ─────────────────────────────

export interface DemandConfig {
  readonly type: "demand";
  groupByOptions: SelectOption[];
  groupBySelectorOptions: (groupBy: string) => SelectOption[];
  variableTypeOptions: MultiSelectOption[];
}

export interface GenerationConfig {
  readonly type: "generation";
  /** Raw generator list from API */
  allGenerators: GeneratorFilterItem[];
  /** Unique generator types derived from allGenerators */
  generatorTypeOptions: SelectOption[];
  /** Unique ownerships filtered by selected types (pass [] for all) */
  ownershipOptionsFor: (selectedTypes: string[]) => SelectOption[];
  /** Generator names filtered by selected types AND selected ownerships */
  generatorOptionsFor: (
    selectedTypes: string[],
    selectedOwnerships: string[],
  ) => MultiSelectOption[];
  variableTypeOptions: SelectOption[];
}

export type DataTypeConfig = DemandConfig | GenerationConfig;

const toOptions = (values: string[]): SelectOption[] =>
  values.map((v) => ({ value: v, label: v }));

const uniqueStrings = (arr: string[]) => [...new Set(arr)];

// ─── Factory — builds config from live API filter data ────────────────────────

export function buildDataTypeConfig(
  filters: FiltersResponse,
): Record<string, DataTypeConfig> {
  const gens = filters.generators;

  return {
    Demand: {
      type: "demand",

      groupByOptions: [
        { value: "total", label: "Total" },
        { value: "discom", label: "Discom" },
      ],

      groupBySelectorOptions: (groupBy) => {
        switch (groupBy) {
          case "discom":
            return toOptions(filters.discoms);
          case "substation":
            return toOptions(filters.substations);
          case "schedule_discom":
            return toOptions(filters.scheduleDiscoms);
          default:
            return [];
        }
      },

      variableTypeOptions: [
        { value: "drawal", label: "Drawal" },
        { value: "schedule", label: "Schedule" },
      ],
    },

    Generation: {
      type: "generation",
      allGenerators: gens,

      generatorTypeOptions: toOptions(
        uniqueStrings(gens.map((g) => g.generatorType)),
      ),

      ownershipOptionsFor: (selectedTypes) => {
        const filtered = selectedTypes.length
          ? gens.filter((g) => selectedTypes.includes(g.generatorType))
          : gens;
        return toOptions(uniqueStrings(filtered.map((g) => g.ownership)));
      },

      generatorOptionsFor: (selectedTypes, selectedOwnerships) => {
        let filtered = gens;
        if (selectedTypes.length) {
          filtered = filtered.filter((g) =>
            selectedTypes.includes(g.generatorType),
          );
        }
        if (selectedOwnerships.length) {
          filtered = filtered.filter((g) =>
            selectedOwnerships.includes(g.ownership),
          );
        }
        return toOptions(uniqueStrings(filtered.map((g) => g.name)));
      },

      variableTypeOptions: [
        { value: "Entitlement", label: "Entitlement" },
        { value: "Expp", label: "Expp" },
        { value: "Experi", label: "Experi" },
      ],
    },
  };
}

// ─── Static fallback used before filters load ─────────────────────────────────

export const dataTypeConfig: Record<string, DataTypeConfig> =
  buildDataTypeConfig({
    generators: [],
    discoms: [],
    scheduleDiscoms: [],
    substations: [],
  });
